import { cn } from '@/lib/cn'
import { useTranslation } from '@/i18n/useTranslation'
import type { ToastVariant, ToastData, ToastProps } from './Toast.types'

// ---------------------------------------------------------------------------
// Variant config
// ---------------------------------------------------------------------------

const VARIANT_STYLES: Record<
  ToastVariant,
  { wrapper: string; iconBg: string; icon: string; bar: string }
> = {
  info: {
    wrapper: 'border-[var(--color-info)]/25',
    iconBg:  'bg-[var(--color-info)]/10 text-[var(--color-info)]',
    icon:    'text-[var(--color-info)]',
    bar:     'bg-[var(--color-info)]',
  },
  error: {
    wrapper: 'border-[var(--color-danger)]/25',
    iconBg:  'bg-[var(--color-danger)]/10 text-[var(--color-danger)]',
    icon:    'text-[var(--color-danger)]',
    bar:     'bg-[var(--color-danger)]',
  },
  success: {
    wrapper: 'border-[var(--color-success)]/25',
    iconBg:  'bg-[var(--color-success)]/10 text-[var(--color-success)]',
    icon:    'text-[var(--color-success)]',
    bar:     'bg-[var(--color-success)]',
  },
  warning: {
    wrapper: 'border-[var(--color-warning)]/25',
    iconBg:  'bg-[var(--color-warning)]/10 text-[var(--color-warning)]',
    icon:    'text-[var(--color-warning)]',
    bar:     'bg-[var(--color-warning)]',
  },
}

// ---------------------------------------------------------------------------
// Toast component
// ---------------------------------------------------------------------------

export function Toast({ toast, onDismiss }: ToastProps) {
  const { t } = useTranslation('ui')
  const styles = VARIANT_STYLES[toast.variant]

  return (
    <div
      role="alert"
      aria-atomic="true"
      className={cn(
        'relative w-full min-w-[288px] max-w-sm overflow-hidden',
        'rounded-list border bg-[var(--color-surface)] shadow-card',
        'pointer-events-auto',
        styles.wrapper,
        toast.removing ? 'animate-toast-out' : 'animate-toast-in',
      )}
    >
      {/* Progress bar — shrinks over duration */}
      <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden rounded-t-list bg-transparent">
        <div
          className={cn('h-full', styles.bar)}
          style={{
            animation: `shrinkWidth ${toast.duration}ms linear forwards`,
          }}
        />
      </div>

      {/* Body */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3.5">
        {/* Icon */}
        <div
          className={cn(
            'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
            styles.iconBg,
          )}
          aria-hidden="true"
        >
          <VariantIcon variant={toast.variant} />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-[var(--color-text-primary)]">
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-0.5 text-xs leading-relaxed text-[var(--color-text-muted)]">
              {toast.description}
            </p>
          )}
        </div>

        {/* Close */}
        <button
          type="button"
          aria-label={t('ui.closeNotification')}
          onClick={() => onDismiss(toast.id)}
          className={cn(
            'mt-0.5 flex-shrink-0 rounded p-0.5 transition-colors',
            'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-control)]',
            'hover:text-[var(--color-text-primary)] focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]',
          )}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------

function VariantIcon({ variant }: { variant: ToastVariant }) {
  switch (variant) {
    case 'info':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 4h2v2h-2V6zm0 4h2v6h-2v-6z" />
        </svg>
      )
    case 'error':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 13v-2h2v2h-2zm0-4V7h2v4h-2z" />
        </svg>
      )
    case 'success':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 15-4-4 1.41-1.41L11 14.17l6.59-6.58L19 9l-8 8z" />
        </svg>
      )
    case 'warning':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      )
  }
}

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

// Re-export types so consumers can import from this path
export type { ToastData, ToastVariant, ToastProps }
